import { useId, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, Search } from 'lucide-react';
import { Pagination } from '@/components/ui';
import { cn } from '@/utils/cn';

export interface FilterableColumn<T> {
  /** Stable key; also used as the property accessor when no `accessor` is given. */
  key: string;
  header: string;
  /** Cell renderer. Defaults to `String(row[key] ?? '')`. */
  render?: (row: T) => ReactNode;
  /**
   * Value used for sorting/filtering. Defaults to `row[key]` cast as needed.
   * Must be a string, number, Date, or null/undefined.
   */
  accessor?: (row: T) => string | number | Date | null | undefined;
  /** Set to `false` to opt out of sort. Defaults to `true`. */
  sortable?: boolean;
  /** Set to `false` to opt out of the per-column text filter. Defaults to `true`. */
  filterable?: boolean;
  /** Optional column width (Tailwind class, e.g. 'w-40'). */
  widthClass?: string;
  /** Header alignment. */
  align?: 'left' | 'right' | 'center';
}

interface FilterableDataTableProps<T> {
  data: T[];
  columns: FilterableColumn<T>[];
  keyExtractor: (row: T) => string;
  /** Placeholder for the global search input above the table. */
  searchPlaceholder?: string;
  /** Pixel height of the scrollable body to mirror Telerik `Height="500px"`. */
  bodyMaxHeightClass?: string;
  /** Initial page size. */
  initialPageSize?: number;
  /** Optional accessible name for the table region. */
  ariaLabel?: string;
  /** Loading state. */
  isLoading?: boolean;
  /** Empty-state message. */
  emptyMessage?: string;
  className?: string;
}

type SortDirection = 'asc' | 'desc';

interface SortState {
  key: string;
  direction: SortDirection;
}

function compareValues(a: unknown, b: unknown): number {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { sensitivity: 'base' });
}

function getAccessor<T>(col: FilterableColumn<T>, row: T): unknown {
  if (col.accessor) return col.accessor(row);
  return (row as Record<string, unknown>)[col.key];
}

function toSearchableString(value: unknown): string {
  if (value == null) return '';
  if (value instanceof Date) return value.toISOString();
  return String(value);
}

/**
 * Accessible data table with column-level text filters, sortable headers,
 * client-side pagination, and a global search box. Mirrors the behaviour of
 * `TelerikGrid` (Sortable, Pageable, FilterMode=FilterMenu, GridSearchBox).
 */
export function FilterableDataTable<T>({
  data,
  columns,
  keyExtractor,
  searchPlaceholder = 'Search…',
  bodyMaxHeightClass = 'max-h-[500px]',
  initialPageSize = 10,
  ariaLabel,
  isLoading,
  emptyMessage = 'No records found.',
  className,
}: FilterableDataTableProps<T>) {
  const [globalSearch, setGlobalSearch] = useState('');
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [sort, setSort] = useState<SortState | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const searchId = useId();

  const filtered = useMemo(() => {
    const globalNeedle = globalSearch.trim().toLowerCase();
    const activeColumnFilters = Object.entries(columnFilters)
      .map(([key, value]) => [key, value.trim().toLowerCase()] as const)
      .filter(([, value]) => value.length > 0);

    return data.filter((row) => {
      // Per-column filters
      for (const [key, needle] of activeColumnFilters) {
        const col = columns.find((c) => c.key === key);
        if (!col) continue;
        const haystack = toSearchableString(getAccessor(col, row)).toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      // Global search across all visible columns
      if (globalNeedle.length > 0) {
        const matches = columns.some((col) => {
          const haystack = toSearchableString(getAccessor(col, row)).toLowerCase();
          return haystack.includes(globalNeedle);
        });
        if (!matches) return false;
      }
      return true;
    });
  }, [data, columns, columnFilters, globalSearch]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return filtered;
    const direction = sort.direction === 'asc' ? 1 : -1;
    return [...filtered].sort(
      (a, b) => compareValues(getAccessor(col, a), getAccessor(col, b)) * direction,
    );
  }, [filtered, sort, columns]);

  const totalRecords = sorted.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * pageSize;
  const pageRows = sorted.slice(pageStart, pageStart + pageSize);

  function toggleSort(col: FilterableColumn<T>) {
    if (col.sortable === false) return;
    setSort((prev) => {
      if (!prev || prev.key !== col.key) return { key: col.key, direction: 'asc' };
      if (prev.direction === 'asc') return { key: col.key, direction: 'desc' };
      return null;
    });
  }

  function setColumnFilter(key: string, value: string) {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  }

  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex justify-end">
        <div className="relative w-full max-w-[250px]">
          <Search
            aria-hidden="true"
            size={16}
            className="absolute left-2 top-1/2 -translate-y-1/2 text-cool-gray-10"
          />
          <label htmlFor={searchId} className="sr-only">
            {searchPlaceholder}
          </label>
          <input
            id={searchId}
            type="search"
            value={globalSearch}
            onChange={(e) => {
              setGlobalSearch(e.target.value);
              setPage(1);
            }}
            placeholder={searchPlaceholder}
            className="block w-full border border-cool-gray-2 py-2 pl-8 pr-3 text-sm shadow-sm placeholder:text-cool-gray-10 focus:border-black focus:outline-none focus:ring-0"
          />
        </div>
      </div>

      <div className={cn('w-full overflow-auto border border-cool-gray-2', bodyMaxHeightClass)}>
        <table
          className="w-full min-w-[600px] border-collapse"
          aria-label={ariaLabel}
          aria-busy={isLoading || undefined}
        >
          <thead className="sticky top-0 z-10 bg-white">
            <tr>
              {columns.map((col) => {
                const isSorted = sort?.key === col.key;
                const ariaSort: 'ascending' | 'descending' | 'none' = isSorted
                  ? sort.direction === 'asc'
                    ? 'ascending'
                    : 'descending'
                  : 'none';
                const alignClass =
                  col.align === 'right'
                    ? 'text-right'
                    : col.align === 'center'
                      ? 'text-center'
                      : 'text-left';
                return (
                  <th
                    key={col.key}
                    aria-sort={col.sortable === false ? undefined : ariaSort}
                    scope="col"
                    className={cn(
                      'px-4 h-14 align-middle bg-white shadow-[inset_0_-1px_0_0_#d0d0ce] whitespace-nowrap',
                      col.widthClass,
                    )}
                  >
                    <div className={cn('flex flex-col gap-2', alignClass)}>
                      {col.sortable === false ? (
                        <span className="text-xs font-semibold text-cool-gray-11">
                          {col.header}
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => toggleSort(col)}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-cool-gray-11 hover:text-black focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-1"
                        >
                          {col.header}
                          {isSorted ? (
                            sort.direction === 'asc' ? (
                              <ArrowUp aria-hidden="true" size={12} />
                            ) : (
                              <ArrowDown aria-hidden="true" size={12} />
                            )
                          ) : (
                            <ArrowUpDown aria-hidden="true" size={12} className="opacity-50" />
                          )}
                        </button>
                      )}
                      {col.filterable !== false ? (
                        <input
                          type="text"
                          value={columnFilters[col.key] ?? ''}
                          onChange={(e) => setColumnFilter(col.key, e.target.value)}
                          aria-label={`Filter ${col.header}`}
                          placeholder="Filter"
                          className="block w-full border border-cool-gray-2 px-2 py-1 text-xs font-normal placeholder:text-cool-gray-10 focus:border-black focus:outline-none focus:ring-0"
                        />
                      ) : (
                        <span className="block h-[26px]" aria-hidden="true" />
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-8 text-center text-sm text-cool-gray-11"
                >
                  Loading…
                </td>
              </tr>
            ) : pageRows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-8 text-center text-sm text-cool-gray-11"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              pageRows.map((row) => (
                <tr key={keyExtractor(row)} className="hover:bg-gray-50">
                  {columns.map((col) => {
                    const alignClass =
                      col.align === 'right'
                        ? 'text-right'
                        : col.align === 'center'
                          ? 'text-center'
                          : 'text-left';
                    return (
                      <td
                        key={col.key}
                        className={cn(
                          'px-4 h-12 text-sm font-normal text-black align-middle bg-white shadow-[inset_0_-1px_0_0_#d0d0ce] whitespace-nowrap',
                          alignClass,
                        )}
                      >
                        {col.render
                          ? col.render(row)
                          : toSearchableString(getAccessor(col, row))}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        page={safePage}
        pageSize={pageSize}
        totalRecords={totalRecords}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
      />
    </div>
  );
}
